import { GlobalToken, Spin, theme, Typography } from "antd";
import { CustomError, Question, SurveyModel } from "survey-core";
import { useQuery } from "@tanstack/react-query";
import { createStyles } from "antd-style";
import { useEffect, useReducer, useRef, useState } from "react";
import { Button } from "../components/Button";
import { ExternalLinkIcon } from "../registrations/icons/ExternalLinkIcon";
import { isAxiosError } from "axios";
import { normalisePostalCode } from "../helpers/normalisePostalCode";

export class SelectorQuestionModel extends Question {
  constructor(name: string) {
    super(name);
    // The raw `glassDamageLocation` survey field can hold an intermediate grouping value
    // (e.g. "side_window") that is not a valid GlassDamageLocation enum value. The
    // `glassDamageLocation:expression` property lets the survey config bind the already
    // refined value (e.g. "{resolvedGlassDamageLocation}") so the selector always forwards
    // a value the backend accepts.
    this.addExpressionProperty(
      "glassDamageLocation",
      (_obj, result: unknown) => {
        this.setPropertyValue(
          "glassDamageLocationValue",
          result === null || result === undefined || result === ""
            ? null
            : String(result),
        );
      },
    );
    // The `servicePartnerCollection` expression property lets the survey config decide which
    // service partner collection the selector queries (e.g. a smart repair specific collection).
    // When the property is not configured, or resolves to an empty value, the component falls
    // back to the `servicePartnerCollection` survey value.
    this.addExpressionProperty(
      "servicePartnerCollection",
      (_obj, result: unknown) => {
        this.setPropertyValue(
          "servicePartnerCollectionValue",
          result === null || result === undefined || result === ""
            ? null
            : String(result),
        );
      },
    );
  }

  getType() {
    return "selector";
  }

  search() {
    this.setPropertyValue("searchRequestedAt", Date.now());
  }

  public setValue(value: string): void {
    super.setValueCore(value);
  }

  get glassDamageLocationValue(): string | null {
    return (
      (this.getPropertyValue("glassDamageLocationValue") as string) ?? null
    );
  }

  get servicePartnerCollectionValue(): string | null {
    return (
      (this.getPropertyValue("servicePartnerCollectionValue") as string) ?? null
    );
  }
}

interface SelectorQuestionProps {
  question: SelectorQuestionModel;
}

const styleUtils = createStyles(
  ({ css }, { themeToken }: { themeToken: GlobalToken }) => {
    const itemBase = `
    border-radius: ${themeToken.borderRadius}px;
    padding: 16px 20px 20px;
    min-height: 120px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-bottom: 24px;
    transition: 0.3s all;
    background-color: #fff;

    &:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
  `;

    return {
      item: css`
      ${itemBase}
      border: 1px solid #e4e4e4;
    `,
      itemSelected: css`
      ${itemBase}
        border: 2px solid ${themeToken.colorPrimaryActive};
    `,
      buttonSelected: css`
        background-color: ${themeToken.colorPrimaryActive};
        border-color: ${themeToken.colorPrimaryActive};

      &:hover,
      &:focus {
          background-color: ${themeToken.colorPrimary};
          border-color: ${themeToken.colorPrimary};
      }
    `,
      preferredPartnerBadge: css`
        border: 2px solid ${themeToken.colorPrimary};
        border-radius: 8px;
        gap: 8px;
        display: inline-flex;
        height: 28px;
        align-items: center;
        font-size: 12px;
        font-weight: 600;
        color: ${themeToken.colorPrimary};
        padding: 0 6px;
    `,
      preferredPartnerBadgeWrapper: css`
        order: 0;
        margin-bottom: 10px;

        @media (min-width: 992px) {
          order: 2;
          margin-left: auto;
          margin-bottom: 0;
        }
    `,
      headerImageContainer: css`
        border-radius: ${themeToken.borderRadius}px;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 12px;
    `,
      headerImage: css`
        width: 100%;
        object-fit: cover;
        border-radius: ${themeToken.borderRadius}px;
    `,
      itemContentHeader: css`
        display: flex;
        gap: 16px;
        flex-direction: column;
        margin-bottom: 15px;

        @media (min-width: 992px) {
          flex-direction: row;
        }
    `,
      uspGrid: css`
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
        margin-bottom: 20px;

        @media (min-width: 576px) {
          grid-template-columns: 1fr 1fr;
        }
    `,
      uspBadge: css`
        display: flex;
        align-items: center;
        border: 1px solid #e8e8e8;
        border-radius: 12px;
        padding: 6px 10px;
    `,
      uspCheckmark: css`
        min-width: 24px;
        display: flex;
        justify-content: center;
        font-size: 13px;
        font-weight: 700;
        color: #52c41a;
        margin-right: 4px;
    `,
      uspText: css`
        font-size: 12px;
        font-weight: 700;
        line-height: 1.4;
        word-break: break-word;
        hyphens: auto;
    `,
    };
  },
);

export const SelectorQuestion = (props: SelectorQuestionProps) => {
  const { token } = theme.useToken();
  const { styles } = styleUtils({ themeToken: token });
  const [showAll, setShowAll] = useState(false);
  const maxVisibleItems: number =
    props.question.getPropertyValue("visibleOptions") || 3;
  const headerImageMaxHeight: number =
    props.question.getPropertyValue("headerImageMaxHeight") || 100;

  const survey = props.question.survey as SurveyModel;
  const queryId = props.question.getPropertyValue("queryId");
  if (!queryId) {
    throw new Error(
      "SelectorQuestionModel requires a queryId property to be set.",
    );
  }

  // The resolved `glassDamageLocation` and `servicePartnerCollection` values are only updated
  // through `setPropertyValue` (see the expression properties in SelectorQuestionModel), so the
  // component needs to explicitly listen for changes to re-render when they resolve.
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const question = props.question;
    question.registerFunctionOnPropertiesValueChanged(
      ["glassDamageLocationValue", "servicePartnerCollectionValue"],
      forceRender,
      "selector-glass-damage-location-resolved",
    );
    return () => {
      question.unRegisterFunctionOnPropertiesValueChanged(
        ["glassDamageLocationValue", "servicePartnerCollectionValue"],
        "selector-glass-damage-location-resolved",
      );
    };
  }, [props.question]);

  const postalCode = normalisePostalCode(
    survey.getValue("selectorPostalCode") || survey.getValue("postalCode"),
  );
  const streetNumber =
    survey.getValue("selectorStreetNumber") || survey.getValue("streetNumber");
  // Free-text alternative to the postal code + street number pair: the backend resolves it to
  // either a four-digit postal code or a city name (see ResultCollectionProvider::filtersToCity).
  const referenceCity =
    (
      survey.getValue("selectorReferenceCity") ||
      survey.getValue("referenceCity") ||
      ""
    ).trim() || undefined;
  // The `servicePartnerCollection` expression property (when configured) decides which
  // collection to query; it falls back to the `servicePartnerCollection` survey value set by
  // the widget configuration or the evaluation engine.
  const presetServicePartnerCollection =
    survey.getValue("servicePartnerCollection") || undefined;
  const servicePartnerCollection =
    props.question.servicePartnerCollectionValue?.trim() ||
    presetServicePartnerCollection;
  const servicePartner = survey.getValue("servicePartner");
  const leaseCompany = survey.getValue("leaseCompany");
  const licensePlate = survey.getValue("licensePlate") as
    | { country?: string; identifier?: string }
    | undefined;
  const licensePlateIdentifier = licensePlate?.identifier?.trim() || undefined;
  const licensePlateCountry = licensePlate?.country?.trim() || undefined;
  const glassDamageLocation =
    props.question.glassDamageLocationValue?.trim() || undefined;
  const searchRequestedAt =
    props.question.getPropertyValue("searchRequestedAt");
  const searchWithoutInput: boolean =
    props.question.getPropertyValue("searchWithoutInput") || false;

  // When a servicePartner or servicePartnerCollection is pre-set, enable without address input.
  // An expression-resolved collection does not count: it narrows an address search rather than
  // replacing it, so it must not fire the query before the driver has searched.
  const hasServicePartnerContext = !!(
    servicePartner || presetServicePartnerCollection
  );
  const hasAddressSearch = !!(
    ((postalCode && streetNumber) || referenceCity) &&
    searchRequestedAt
  );
  const queryEnabled =
    hasAddressSearch || hasServicePartnerContext || searchWithoutInput;

  const selectorQuery = useQuery({
    queryKey: [
      "selector",
      queryId,
      postalCode,
      streetNumber,
      referenceCity,
      searchRequestedAt,
      servicePartner,
      servicePartnerCollection,
      leaseCompany,
      licensePlateIdentifier,
      licensePlateCountry,
      glassDamageLocation,
    ],
    queryFn: async () => {
      return survey!
        .api!.get(`driver/selector/${queryId}`, {
          params: {
            country: "NL",
            postalCode: postalCode || undefined,
            streetNumber: streetNumber || undefined,
            referenceCity,
            servicePartner: servicePartner || undefined,
            servicePartnerCollection: servicePartnerCollection || undefined,
            leaseCompany: leaseCompany || undefined,
            licensePlateIdentifier,
            licensePlateCountry,
            glassDamageLocation: glassDamageLocation || undefined,
          },
        })
        .then((response: any) => response.data);
    },
    enabled: queryEnabled,
    retry: false,
  });

  const errorMap = useRef<{ [key: number]: CustomError }>({
    422: new CustomError(
      "De ingevoerde adres gegevens zijn ongeldig. Controleer de adres gegevens en probeer het opnieuw.",
    ),
    404: new CustomError(
      "De ingevoerde adres gegevens zijn onbekend. Controleer de adres gegevens en probeer het opnieuw.",
    ),
  });

  useEffect(() => {
    if (
      selectorQuery.isError &&
      isAxiosError(selectorQuery.error) &&
      errorMap.current[selectorQuery.error.status!]
    ) {
      props.question.addError(errorMap.current[selectorQuery.error.status!]);
    } else {
      props.question.clearErrors();
    }
  }, [selectorQuery.isError, selectorQuery.error, props.question]);

  const selectorItems = selectorQuery.data?.member ?? [];
  const selectedItemIndex = selectorItems.findIndex((item: any) =>
    isItemSelected(item),
  );

  // Auto-select the matching item when servicePartner is pre-set
  useEffect(() => {
    if (!servicePartner || selectorItems.length === 0) return;
    if (props.question.value) return;

    const match = selectorItems.find(
      (item: any) =>
        item.id === servicePartner ||
        item.relatedServicePartners?.includes(servicePartner),
    );
    if (match) {
      props.question.setValue(match.id);
      survey.setValue("selector__name", match.fullName);
      survey.setValue("selector__address", {
        street: match.address?.street,
        streetNumber: match.address?.streetNumber,
        streetNumberAddition: match.address?.streetNumberAddition,
        postalCode: match.address?.postalCode,
        city: match.address?.city,
        country: match.address?.country,
      });
    }
  }, [servicePartner, selectorItems]);

  useEffect(() => {
    if (selectedItemIndex >= maxVisibleItems && !showAll) {
      setShowAll(true);
    }
  }, [selectedItemIndex, showAll]);

  function getSelectorItemMapsUrl(item: any): string {
    const parts = [
      item.name || "",
      item.address?.street || "",
      item.address?.streetNumber || "",
      item.address?.streetNumberAddition || "",
      ",",
      item.address?.postalCode || "",
      item.address?.city || "",
    ];
    const destination = parts.join(" ").replace(/ +/g, " ").trim();
    const encoded = encodeURIComponent(destination);

    return `https://www.google.nl/maps?saddr=&daddr=${encoded}`;
  }

  function isItemSelected(item: any): boolean {
    return survey.getValue("selector") == item.id;
  }

  const visibleItems = showAll
    ? selectorItems
    : selectorItems.slice(0, maxVisibleItems);

  const handleClick = (item: any) => {
    let servicePartner = null;
    if (item?.businessType === "dealership") {
      survey.setValue("dealership", item.id);
      // Select the first related service partner for dealerships
      servicePartner = item.relatedServicePartners[0];
    } else {
      servicePartner = item.id;
    }

    //TODO: See if this logic can be handled by backend
    survey.setValue("selector__name", item.fullName);
    survey.setValue("selector__address", {
      street: item.address?.street,
      streetNumber: item.address?.streetNumber,
      streetNumberAddition: item.address?.streetNumberAddition,
      postalCode: item.address?.postalCode,
      city: item.address?.city,
      country: item.address?.country,
    });

    survey.setValue("servicePartner", servicePartner);
    props.question.setValue(item.id);
    survey.nextPage();
  };

  return (
    <div>
      {selectorQuery.isLoading && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Typography.Text
            style={{
              display: "block",
              marginBottom: 16,
              fontWeight: 500,
              color: token.colorTextSecondary,
            }}
          >
            Bezig met laden...
          </Typography.Text>
          <Spin />
        </div>
      )}
      {selectorQuery.data ? (
        <div>
          {selectorItems.length > 0 && selectedItemIndex === -1 ? (
            <Typography.Title
              style={{
                color: token.colorPrimary,
                fontWeight: 700,
                marginBottom: 20,
              }}
              level={3}
            >
              Selecteer vestiging
            </Typography.Title>
          ) : selectorItems.length === 0 ? (
            "Er zijn geen vestigingen gevonden."
          ) : null}
          {visibleItems.map((item: any) => (
            <div
              key={item.id}
              className={
                isItemSelected(item) ? styles.itemSelected : styles.item
              }
            >
              {item.headerImage?.contentUrl && (
                <div
                  className={styles.headerImageContainer}
                  style={{ maxHeight: headerImageMaxHeight }}
                >
                  <img
                    src={item.headerImage.contentUrl}
                    alt={item.fullName || item.name || "Service Partner"}
                    className={styles.headerImage}
                    style={{ maxHeight: headerImageMaxHeight }}
                  />
                </div>
              )}
              <div className={styles.itemContentHeader}>
                {item.tags?.length > 0 && (
                  <div className={styles.preferredPartnerBadgeWrapper}>
                    <div className={styles.preferredPartnerBadge}>
                      <div style={{ width: 20, height: 20 }}>
                        <StarIcon />
                      </div>
                      <span>{item.tags[0]?.name}</span>
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 16 }}>
                  <div style={{ width: 32, height: 32 }}>
                    <LocationIcon />
                  </div>
                  <div>
                    <Typography.Title
                      level={5}
                      style={{ margin: 0, fontWeight: 700, paddingTop: 2 }}
                    >
                      {item.fullName}
                    </Typography.Title>
                    {item.address?.street &&
                      item?.address?.streetNumber &&
                      item?.address?.postalCode &&
                      item?.address?.city && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <Typography.Text
                            style={{ color: "#575757", fontWeight: 600 }}
                          >
                            {item.address?.street} {item.address?.streetNumber},{" "}
                            {item.address?.postalCode} {item.address?.city}
                          </Typography.Text>
                          <a
                            href={getSelectorItemMapsUrl(item)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              color: "#575757",
                            }}
                          >
                            <ExternalLinkIcon />
                          </a>
                        </span>
                      )}
                  </div>
                </div>
              </div>
              {item.travelDistanceInMeter ? (
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    marginBottom: 20,
                  }}
                >
                  <div style={{ width: 32, height: 32 }}>
                    <RouteIcon />
                  </div>

                  <div>
                    <Typography.Text
                      style={{ color: "#575757", fontWeight: 600 }}
                    >
                      Afstand:{" "}
                      {(item.travelDistanceInMeter / 1000).toLocaleString(
                        "nl-NL",
                        { maximumFractionDigits: 1 },
                      )}{" "}
                      km
                    </Typography.Text>
                  </div>
                </div>
              ) : null}

              {item.uniqueSellingPoints?.length > 0 && (
                <div className={styles.uspGrid}>
                  {item.uniqueSellingPoints.map((usp: string) => (
                    <div key={usp} className={styles.uspBadge}>
                      <span className={styles.uspCheckmark}>✓</span>
                      <span className={styles.uspText}>{usp}</span>
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={() => handleClick(item)}
                type="primary"
                className={isItemSelected(item) ? styles.buttonSelected : ""}
              >
                {isItemSelected(item)
                  ? "Geselecteerd"
                  : "Selecteer deze vestiging"}
              </Button>
            </div>
          ))}
          {selectorItems.length > maxVisibleItems ? (
            <Button
              type="default"
              onClick={() => setShowAll((prev) => !prev)}
              style={{ marginTop: 12, width: "100%" }}
            >
              {showAll ? "Toon minder vestigingen" : "Toon meer vestigingen"}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// TODO replace with FA icons
const LocationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
    <path d="M528 320C528 205.1 434.9 112 320 112C205.1 112 112 205.1 112 320C112 434.9 205.1 528 320 528C434.9 528 528 434.9 528 320zM64 320C64 178.6 178.6 64 320 64C461.4 64 576 178.6 576 320C576 461.4 461.4 576 320 576C178.6 576 64 461.4 64 320zM297.4 342.6L150.3 308.7L433.1 206.9L331.3 489.7L297.4 342.6z" />
  </svg>
);

const RouteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
    <path d="M502.6 219.7C495.4 231.2 487.6 242.3 480 252.4C472.5 242.3 464.6 231.2 457.4 219.7C439.7 191.3 432 170.3 432 160C432 133.5 453.5 112 480 112C506.5 112 528 133.5 528 160C528 170.3 520.3 191.3 502.6 219.7zM491.4 315C516.8 285.1 576 210.2 576 160C576 107 533 64 480 64C427 64 384 107 384 160C384 193.3 409.9 237.3 434.8 272L392 272C343.4 272 304 311.4 304 360C304 408.6 343.4 448 392 448L488 448C510.1 448 528 465.9 528 488C528 510.1 510.1 528 488 528L241.8 528C239.1 531.8 236.3 535.6 233.6 539.2C222.9 553.4 212.3 566.1 203.6 576L488 576C536.6 576 576 536.6 576 488C576 439.4 536.6 400 488 400L392 400C369.9 400 352 382.1 352 360C352 337.9 369.9 320 392 320L476.9 320C482 321.1 487.6 319.4 491.4 315zM182.9 472.4C175.6 483.4 167.6 494.1 160 503.6C152.4 494.1 144.4 483.4 137.1 472.4C119.4 445.7 112 425.9 112 416C112 389.5 133.5 368 160 368C186.5 368 208 389.5 208 416C208 425.9 200.6 445.7 182.9 472.4zM201.9 528C227.8 494.9 256 450.7 256 416C256 363 213 320 160 320C107 320 64 363 64 416C64 466.5 123.8 537 149 564.4C155 570.9 165 570.9 170.9 564.4C171 564.3 171.1 564.2 171.2 564.1C171.5 563.8 171.8 563.4 172.1 563.1C174.9 560 178.1 556.4 181.6 552.4C181.7 552.3 181.7 552.3 181.8 552.2C187.9 545.2 194.8 537 201.8 528z" />
  </svg>
);

const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="#ffa200">
    <path d="M320.1 32C329.1 32 337.4 37.1 341.5 45.1L415 189.3L574.9 214.7C583.8 216.1 591.2 222.4 594 231C596.8 239.6 594.5 249 588.2 255.4L473.7 369.9L499 529.8C500.4 538.7 496.7 547.7 489.4 553C482.1 558.3 472.4 559.1 464.4 555L320.1 481.6L175.8 555C167.8 559.1 158.1 558.3 150.8 553C143.5 547.7 139.8 538.8 141.2 529.8L166.4 369.9L52 255.4C45.6 249 43.4 239.6 46.2 231C49 222.4 56.3 216.1 65.3 214.7L225.2 189.3L298.8 45.1C302.9 37.1 311.2 32 320.2 32zM320.1 108.8L262.3 222C258.8 228.8 252.3 233.6 244.7 234.8L119.2 254.8L209 344.7C214.4 350.1 216.9 357.8 215.7 365.4L195.9 490.9L309.2 433.3C316 429.8 324.1 429.8 331 433.3L444.3 490.9L424.5 365.4C423.3 357.8 425.8 350.1 431.2 344.7L521 254.8L395.5 234.8C387.9 233.6 381.4 228.8 377.9 222L320.1 108.8z" />
  </svg>
);
